import defaultState from './defaultState';
import * as C from '../../constants';
import SkiAreasService from '../../../../services/ski/areas-service';
import converter from '../../../../utils/converter';

export default {

  /**
   * Resets the ski module to it's original state.
   *
   * @param commit
   */
  resetState({ commit }) {
    commit(C.SKI_STATE_RESET, defaultState());
  },

  /**
   * Fetches a list of areas with their ski information.
   *
   * @param commit
   * @returns {Promise<T | never>}
   */
  fetchAreas({ commit }) {
    commit(C.SKI_AREAS_LIST_PENDING);
    return SkiAreasService.getSkiAreas()
      .then((res) => {
        commit(C.SKI_AREAS_LIST_SUCCESS, res.data);
        return Promise.resolve(res.data);
      })
      .catch((err) => {
        commit(C.SKI_AREAS_LIST_FAILURE);
        return Promise.reject(err.response.data);
      });
  },

  /**
   * Fetches a list of ski facts for an area.
   *
   * @param commit
   * @param id
   * @returns {Promise<T | never>}
   */
  fetchSkiFacts({ commit }, id) {
    commit(C.SKI_FACTS_GET_PENDING);

    const promises = Promise.all([
      SkiAreasService.getSkiFactsForArea(id),
      SkiAreasService.getSkiFactUnits(),
      SkiAreasService.getArea(id),
    ]);

    return promises
      .then((res) => {
        commit(C.SKI_FACTS_GET_SUCCESS, res);
        return Promise.resolve(res);
      })
      .catch((err) => {
        commit(C.SKI_FACTS_GET_FAILURE, err);
        return Promise.reject(err);
      });
  },

  /**
   * Updates ski facts for an area.
   *
   * @param commit
   * @param id
   * @param data
   * @returns {Promise<T | never>}
   */
  updateSkiFacts({ commit }, { id, data }) {
    commit(C.SKI_FACTS_UPDATE_PENDING);
    return SkiAreasService.updateSkiFactsForArea(id, data)
      .then((res) => {
        commit(C.SKI_FACTS_UPDATE_SUCCESS, res.data);
        return Promise.resolve(res.data);
      })
      .catch((err) => {
        commit(C.SKI_FACTS_UPDATE_FAILURE, err.response.data);
        return Promise.reject(err.response.data);
      });
  },

  /**
   * Fetches a snow guarantee for an area
   *
   * @param commit
   * @param id
   * @returns {Promise<T | never>}
   */
  fetchSnowGuarantee({ commit }, id) {
    commit(C.SKI_SNOW_GUARANTEE_GET_PENDING);

    const promises = Promise.all([
      SkiAreasService.getSnowGuaranteeForArea(id),
      SkiAreasService.getArea(id),
    ]);

    return promises
      .then((res) => {
        commit(C.SKI_SNOW_GUARANTEE_GET_SUCCESS, res);
        return Promise.resolve(res);
      })
      .catch((err) => {
        commit(C.SKI_SNOW_GUARANTEE_GET_FAILURE, err);
        return Promise.reject(err);
      });
  },

  /**
   * Updates snow guarantee for an area
   *
   * @param commit
   * @param id
   * @param payload
   */
  updateSnowGuarantee({ commit }, { id, payload }) {
    commit(C.SKI_SNOW_GUARANTEE_UPDATE_PENDING);

    return SkiAreasService.updateSnowGuaranteeForArea(id, payload)
      .then(() => {
        commit(C.SKI_SNOW_GUARANTEE_UPDATE_SUCCESS);
      })
      .catch((err) => {
        commit(C.SKI_SNOW_GUARANTEE_UPDATE_FAILURE, err);
      });
  },

  /**
   * Sets snow guarantee for an area.
   *
   * @param commit
   * @param payload
   */
  setSnowGuarantee({ commit }, payload) {
    commit(C.SKI_SNOW_GUARANTEE_SET, payload);
  },

  /**
   * Sets a ski fact
   *
   * @param commit
   * @param index
   * @param payload
   * @param data
   */
  setSkiFact({ commit }, { index, payload }) {
    commit(C.SKI_FACT_SET, { index, payload });
  },

  /**
   *
   * @param commit
   * @param payload
   */
  checkAllSkiFacts({ commit }, payload) {
    commit(C.SKI_FACT_CHECK_ALL, payload);
  },

  /**
   * Checks/un-checks all ski facts that has a value.
   *
   * @param commit
   */
  checkValuedSkiFacts({ commit }) {
    commit(C.SKI_FACT_CHECK_VALUED);
  },

  /**
   * Fetch slope maps for area id.
   *
   * @param commit
   * @param payload
   * @returns {Promise<T | never>}
   */
  fetchSlopeMaps({ commit }, payload) {
    commit(C.SKI_SLOPE_MAPS_PENDING);
    return SkiAreasService.fetchSlopeMaps(payload).then((res) => {
      commit(C.SKI_SLOPE_MAPS_SUCCESS, res.data);
    }).catch(() => {
      commit(C.SKI_SLOPE_MAPS_FAILURE);
    });
  },

  /**
   * Upload image to server.
   *
   * @param commit
   * @param payload
   */
  uploadImage({ commit }, payload) {
    commit(C.SKI_SLOPE_MAP_ADD_PENDING);
    return SkiAreasService.uploadSlopeMap(
      payload.id,
      payload.image,
      payload.priority,
    ).then((res) => {
      commit(C.SKI_SLOPE_MAP_ADD_SUCCESS, res.data);
    }).catch(() => {
      commit(C.SKI_SLOPE_MAP_ADD_FAILURE);
    });
  },

  /**
   * Delete image from server.
   *
   * @param commit
   * @param payload
   */
  removeImage({ commit }, payload) {
    commit(C.SKI_SLOPE_MAP_REMOVE_PENDING);
    return SkiAreasService.removeSlopeMap(payload.id, payload.image).then(() => {
      const filteredImages = [];
      const targetId = payload.image.id;
      payload.images.forEach((image) => {
        if (targetId !== image.id) {
          filteredImages.push(image);
        }
      });
      commit(C.SKI_SLOPE_MAP_REMOVE_SUCCESS, filteredImages);
    }).catch(() => {
      commit(C.SKI_SLOPE_MAP_REMOVE_FAILURE);
    });
  },

  /**
   * Rearrange the image list.
   *
   * @param commit
   * @param payload
   */
  rearrangeImageList({ commit }, payload) {
    commit(C.SKI_SLOPE_MAP_UPDATE_PENDING);
    const imageList = converter.rearrangeImageList(payload.images);
    return SkiAreasService.updateSlopeMaps(payload.id, imageList).then(() => {
      commit(C.SKI_SLOPE_MAP_UPDATE_SUCCESS, payload.images);
    }).catch(() => {
      commit(C.SKI_SLOPE_MAP_UPDATE_FAILURE);
    });
  },

  /**
   * Get area by id.
   *
   * @param commit
   * @param payload
   * @returns {Promise<T | never>}
   */
  fetchArea({ commit }, payload) {
    commit(C.SKI_AREA_PENDING);
    return SkiAreasService.getArea(payload).then((res) => {
      commit(C.SKI_AREA_SUCCESS, res.data);
    }).catch(() => {
      commit(C.SKI_AREA_FAILURE);
    });
  },

  /**
   * Update image.
   *
   * @param commit
   * @param state
   * @param payload
   */
  imageUpdate({ commit, state }, payload) {
    commit(C.SKI_SLOPE_MAP_UPDATE_PENDING);
    const imageList = state.slopeMaps.items;
    const updatedImageList = converter.updateImageList(imageList, payload.image, payload.index);
    return SkiAreasService.updateSlopeMaps(payload.id, updatedImageList).then(() => {
      commit(C.SKI_SLOPE_MAP_UPDATE_SUCCESS, updatedImageList);
    }).catch(() => {
      commit(C.SKI_SLOPE_MAP_UPDATE_FAILURE);
    });
  },

  fetchPickupPlaces({ commit, dispatch }, id) {
    dispatch('fetchArea', id);
    commit(C.SKI_PICKUP_PLACES_GET_PENDING);
    return SkiAreasService.getPickupPlaces(id).then((res) => {
      if (res.status === 204) {
        commit(C.SKI_PICKUP_PLACES_GET_SUCCESS, []);
      } else {
        commit(C.SKI_PICKUP_PLACES_GET_SUCCESS, res.data);
      }
    }).catch(() => {
      commit(C.SKI_PICKUP_PLACES_GET_FAILURE);
    });
  },

  updatePickupPlaces({ commit, state }, payload) {
    commit(C.SKI_PICKUP_PLACES_UPDATE_PENDING);
    return SkiAreasService.updatePickupPlaces(state.area.id, payload).then((res) => {
      commit(C.SKI_PICKUP_PLACES_UPDATE_SUCCESS, res.data);
    }).catch(() => {
      commit(C.SKI_PICKUP_PLACES_UPDATE_FAILURE);
    });
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/ski/areas/actions.js